import { useMemo } from 'react';

import { useAppSelector } from '@store/store.tsx';
import {
  selectSiteSettings,
  type CatalogFlagSettings,
  type CatalogLabelSettings,
} from '@store/slices/settingsSlice.tsx';

export interface ResolvedCatalogLabels {
  inStockBadge: string;
  backorderBadge: string;
  backorderMessage: string;
  outOfStockBadge: string;
  outOfStockMessage: string;
  unknownBadge: string;
  unknownMessage: string;
  notifyMeCta: string;
  blockedMessage: string;
  oversaleMessage: string;
}

export interface ResolvedCatalogFlags {
  hideBackorderLabel: boolean;
}

export interface SiteLabelsResult {
  catalog: {
    labels: ResolvedCatalogLabels;
    flags: ResolvedCatalogFlags;
  };
}

const preferString = (
  candidate: string | null | undefined,
  fallback: string,
  options?: { allowEmpty?: boolean },
): string => {
  if (typeof candidate !== 'string') {
    return fallback;
  }

  const trimmed = candidate.trim();
  if (trimmed.length === 0) {
    return options?.allowEmpty ? '' : fallback;
  }

  return trimmed;
};

const preferBoolean = (candidate: boolean | null | undefined, fallback: boolean): boolean =>
  typeof candidate === 'boolean' ? candidate : fallback;

export const DEFAULT_CATALOG_LABELS: ResolvedCatalogLabels = {
  inStockBadge: 'Disponible',
  backorderBadge: 'Disponible bajo pedido',
  backorderMessage: 'Podés completar la compra y coordinamos la entrega apenas ingrese stock.',
  outOfStockBadge: 'Sin stock',
  outOfStockMessage: 'Temporalmente sin stock. Suscribite para recibir alertas.',
  unknownBadge: 'Disponibilidad a confirmar',
  unknownMessage: 'Estamos confirmando stock. Dejanos tus datos y te avisamos.',
  notifyMeCta: '', //'Avisarme',
  blockedMessage: 'Temporalmente sin stock. Suscribite para recibir alertas.',
  oversaleMessage: 'Disponible bajo pedido',
};

export const DEFAULT_CATALOG_FLAGS: ResolvedCatalogFlags = {
  hideBackorderLabel: true,   // TODO: En MVP oculto los flags hasta completar la implementación de la lógica e interfaz de configuración
};

export const DEFAULT_SITE_LABELS: SiteLabelsResult = {
  catalog: {
    labels: DEFAULT_CATALOG_LABELS,
    flags: DEFAULT_CATALOG_FLAGS,
  },
};

export const resolveCatalogLabels = (
  labels: CatalogLabelSettings | null | undefined,
): ResolvedCatalogLabels => ({
  inStockBadge: preferString(labels?.in_stock_badge, DEFAULT_CATALOG_LABELS.inStockBadge),
  backorderBadge: preferString(labels?.backorder_badge, DEFAULT_CATALOG_LABELS.backorderBadge),
  backorderMessage: preferString(
    labels?.backorder_message,
    DEFAULT_CATALOG_LABELS.backorderMessage,
    { allowEmpty: true },
  ),
  outOfStockBadge: preferString(labels?.out_of_stock_badge, DEFAULT_CATALOG_LABELS.outOfStockBadge),
  outOfStockMessage: preferString(
    labels?.out_of_stock_message,
    DEFAULT_CATALOG_LABELS.outOfStockMessage,
    { allowEmpty: true },
  ),
  unknownBadge: preferString(labels?.unknown_badge, DEFAULT_CATALOG_LABELS.unknownBadge),
  unknownMessage: preferString(
    labels?.unknown_message,
    DEFAULT_CATALOG_LABELS.unknownMessage,
    { allowEmpty: true },
  ),
  notifyMeCta: preferString(
    labels?.notify_me_cta,
    DEFAULT_CATALOG_LABELS.notifyMeCta,
    { allowEmpty: true },
  ),
  blockedMessage: preferString(
    labels?.blocked_message,
    DEFAULT_CATALOG_LABELS.blockedMessage,
    { allowEmpty: true },
  ),
  oversaleMessage: preferString(
    labels?.oversale_message,
    DEFAULT_CATALOG_LABELS.oversaleMessage,
    { allowEmpty: true },
  ),
});

export const resolveCatalogFlags = (
  flags: CatalogFlagSettings | null | undefined,
): ResolvedCatalogFlags => ({
  hideBackorderLabel: preferBoolean(flags?.hide_backorder_label ?? undefined, DEFAULT_CATALOG_FLAGS.hideBackorderLabel),
});

export const useSiteLabels = (): SiteLabelsResult => {
  const site = useAppSelector(selectSiteSettings);

  return useMemo(() => {
    const labels = resolveCatalogLabels(site?.catalog_labels);
    const flags = resolveCatalogFlags(site?.catalog_flags);

    return {
      catalog: {
        labels,
        flags,
      },
    } satisfies SiteLabelsResult;
  }, [site]);
};

export default useSiteLabels;
